import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, withModifiers as _withModifiers, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "tasks-title" }
const _hoisted_2 = { class: "tasks" }
const _hoisted_3 = { class: "task__cell task__cell--label" }
const _hoisted_4 = { class: "task__cell task__cell--value" }
const _hoisted_5 = { class: "task__cell task__cell--action" }
const _hoisted_6 = { class: "empty" }
const _hoisted_7 = { class: "empty__text" }
const _hoisted_8 = { key: 0 }

import { computed, ref } from "vue";
import { useQuery, useMutation } from "@vue/apollo-composable";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";
import { Icon } from "@iconify/vue";
import { useI18n } from "vue-i18n";
import { formatPrice } from "@/helpers/utils";
import { GET_ALL_CLIENT_WAITING_ACTIONS } from "@/graphql/client/queries";
import { APPROVE_BUDGET } from "@/graphql/project/mutations";
import TileDashboard from "@/components/TileDashboard.vue";
import TileTitle from "@/components/TileTitle.vue";
import Badge from "@/components/Badge.vue";
import BasicButton from "./BasicButton.vue";
import ConfirmModal from "./ConfirmModal.vue";
import OrderDetails from "./order/OrderDetails.vue";
import { Budget } from "@/helpers/project/typesDefinition";
import PaymentModal from "@/components/PaymentModal.vue";
import { Invoice } from "@/helpers/invoice/typesDefinition";
import ButtonIcon from "@/components/ButtonIcon.vue";
import RecurringOrderDetails from "@/components/order/RecurringOrderDetails.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'TileAllDashboardTasks',
  setup(__props) {

const toast = useToast();
const route = useRoute();

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
});

const { result: actionsResult } = useQuery(GET_ALL_CLIENT_WAITING_ACTIONS);

const items = computed(() => {
  const actions = actionsResult?.value?.clientActions || [];

  return actions.map((item) => {
    if (item.__typename === "BudgetApprobationAction") {
      const value = item.budget.amount;

      return {
        ...item,
        id: `${item.__typename}-${item.budget.id}`,
        label: t("project.budget.label.toApprove", {
          header: `${item.budget.project.name} - `,
        }),
        value,
        valueFormatted: formatPrice(value),
      };
    }
    if (item.__typename === "InvoicePaymentAction") {
      const value = item.invoice.balance;
      return {
        ...item,
        id: `${item.__typename}-${item.invoice.id}`,
        label: t("labels.invoiceToPay", {
          invoiceNumber: item.invoice.invoiceNumber,
        }),
        value,
        valueFormatted: formatPrice(value),
      };
    }
    if (item.__typename === "OrderApprobationAction") {
      const value = item.order.price;
      return {
        ...item,
        id: `${item.__typename}-${item.order.id}`,
        label: t(
          item.order.__typename === "RecurringOrder"
            ? "labels.recurringOrder.toApprove"
            : "labels.order.toApprove",
          {
            orderNumber: item.order.number,
            header: `${item.order.project.name} - `,
          },
        ),
        value,
        valueFormatted: formatPrice(value),
      };
    }
    return item;
  });
});

const refetchQueries = [
  {
    query: GET_ALL_CLIENT_WAITING_ACTIONS,
  },
];

const previousBudget = ref<Budget | undefined>();
const budgetToApprove = ref<Budget | undefined>();

const {
  mutate: approveBudget,
  onDone: onDoneApprove,
  onError: onErrorApprove,
  loading: loadingApprove,
} = useMutation(APPROVE_BUDGET, () => ({
  variables: {
    id: budgetToApprove.value?.id,
  },
  refetchQueries,
}));

onDoneApprove(async (result) => {
  if (result.data?.approveBudget?.status === "ok") {
    isConfirmBudgetApproveModalVisible.value = false;
    toast.success(t("success.budgetapproval"));
  } else {
    const errorResponse = result.data?.approveBudget?.error;
    console.log(errorResponse);
    toast.error(t("errors.budgetapproval", { message: errorResponse }));
  }
});

onErrorApprove((error) => {
  logErrorMessages(error);
  toast.error(t("errors.budgetapproval", { message: t("errors.internal") }));
});

const isConfirmBudgetApproveModalVisible = ref(
  route.name === "ProjectBudgetDetails",
);
const closeConfirmBudgetApproveModal = () => {
  isConfirmBudgetApproveModalVisible.value = false;
};
const openConfirmBudgetApproveModal = (
  budget: Budget,
  projectPreviousBudget?: Budget,
) => {
  budgetToApprove.value = budget;
  previousBudget.value = projectPreviousBudget;
  isConfirmBudgetApproveModalVisible.value = true;
};

const currentOrderId = ref<string | undefined>();
const currentOrderProjectId = ref<string | undefined>();
const isOrderModalDetailsVisible = computed(() =>
  Boolean(currentOrderId.value),
);

const openOrderModal = (orderId: string, projectId: string) => {
  currentOrderId.value = orderId;
  currentOrderProjectId.value = projectId;
};

const closeOrderDetailsModal = () => {
  currentOrderId.value = undefined;
};

const invoiceToPay = ref();
const isPaymentModalVisible = computed(() => !!invoiceToPay.value);
const setInvoiceToPay = (invoice: Invoice) => {
  invoiceToPay.value = invoice;
};
const closeInvoicePaymentModal = () => {
  invoiceToPay.value = undefined;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(TileDashboard, {
      empty: items.value.length <= 0
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(TileTitle, {
            title: _ctx.$t('project.myTasks.title'),
            level: 2
          }, null, 8, ["title"]),
          (items.value.length)
            ? (_openBlock(), _createBlock(Badge, {
                key: 0,
                "color-name": "error",
                label: items.value.length
              }, null, 8, ["label"]))
            : _createCommentVNode("", true)
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("table", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
            return (_openBlock(), _createElementBlock("tr", {
              key: item.id,
              class: "task"
            }, [
              _createElementVNode("td", _hoisted_3, _toDisplayString(item.label), 1),
              _createElementVNode("td", _hoisted_4, _toDisplayString(item.valueFormatted), 1),
              _createElementVNode("td", _hoisted_5, [
                (item.__typename === 'InvoicePaymentAction')
                  ? (_openBlock(), _createBlock(ButtonIcon, {
                      key: 0,
                      label: _unref(t)('buttons.invoices.pay'),
                      "icon-name": "material-symbols:qr-code-2",
                      onClick: _withModifiers(($event: any) => (setInvoiceToPay(item.invoice)), ["prevent"])
                    }, null, 8, ["label", "onClick"]))
                  : _createCommentVNode("", true),
                (item.__typename === 'BudgetApprobationAction')
                  ? (_openBlock(), _createBlock(BasicButton, {
                      key: 1,
                      disabled: _unref(loadingApprove),
                      onClick: _withModifiers(($event: any) => (
                openConfirmBudgetApproveModal(
                  item.budget,
                  item.budget.project.previouslyApprovedBudget,
                )
              ), ["prevent"])
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("buttons.approve.budget")), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled", "onClick"]))
                  : (item.__typename === 'OrderApprobationAction')
                    ? (_openBlock(), _createBlock(BasicButton, {
                        key: 2,
                        disabled: _unref(loadingApprove),
                        onClick: _withModifiers(
                () => openOrderModal(item.order.id, item.order.project.id)
              , ["prevent"]),
                        color: "neutral"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("buttons.order.approve")), 1)
                        ]),
                        _: 1
                      }, 8, ["disabled", "onClick"]))
                    : _createCommentVNode("", true)
              ])
            ]))
          }), 128))
        ])
      ]),
      empty: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_unref(Icon), {
            class: "empty__icon",
            icon: "mdi-medal-outline"
          }),
          _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t("project.myTasks.empty")), 1)
        ])
      ]),
      _: 1
    }, 8, ["empty"]),
    (invoiceToPay.value && isPaymentModalVisible.value)
      ? (_openBlock(), _createBlock(PaymentModal, {
          key: 0,
          invoice: invoiceToPay.value,
          onClose: closeInvoicePaymentModal
        }, null, 8, ["invoice"]))
      : _createCommentVNode("", true),
    (budgetToApprove.value && isConfirmBudgetApproveModalVisible.value)
      ? (_openBlock(), _createBlock(ConfirmModal, {
          key: 1,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (closeConfirmBudgetApproveModal())),
          onNo: _cache[1] || (_cache[1] = ($event: any) => (closeConfirmBudgetApproveModal())),
          onYes: _cache[2] || (_cache[2] = ($event: any) => (_unref(approveBudget)())),
          question: _unref(t)('modal.budgetapproval.question')
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(_unref(t)("modal.budgetapproval.content.newBudget", {
          budget: _unref(formatPrice)(budgetToApprove.value.amount),
        })), 1),
            (previousBudget.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_unref(t)("modal.budgetapproval.content.previousBudget", {
          previousBudget: _unref(formatPrice)(previousBudget.value.amount),
        })), 1))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["question"]))
      : _createCommentVNode("", true),
    (currentOrderId.value && currentOrderProjectId.value && isOrderModalDetailsVisible.value)
      ? (_openBlock(), _createBlock(RecurringOrderDetails, {
          key: 2,
          id: currentOrderId.value,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (closeOrderDetailsModal())),
          projectId: currentOrderProjectId.value,
          "has-client-access": true,
          hasAdminAccess: false
        }, null, 8, ["id", "projectId"]))
      : _createCommentVNode("", true),
    (currentOrderId.value && currentOrderProjectId.value && isOrderModalDetailsVisible.value)
      ? (_openBlock(), _createBlock(OrderDetails, {
          key: 3,
          id: currentOrderId.value,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (closeOrderDetailsModal())),
          projectId: currentOrderProjectId.value,
          "has-client-access": true,
          hasAdminAccess: false
        }, null, 8, ["id", "projectId"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})